import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import JSZip from 'jszip';
import mime from 'mime';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Platform } from 'react-native';

import { Document, LocalFile } from '@graphql/generated';
import { usePreviewDocuments } from '@hooks/usePreviewDocuments';
import { useSaveImage } from '@hooks/useSaveImage';
import { saveToLegacyAndroidDownloads } from '@utils/androidLegacyDownloadDir';
import { fetchWithErrorHandling } from '@utils/fetchWithErrorHandling';
import { AsyncStorage } from '@utils/storage';

// react-native-blob-util is native-only. This hook/provider is used by the
// web download path too, so a top-level import would pull a native module
// into the web bundle even though the branch that uses it never runs there
// (Copilot review, PR #5298) -- guard it the same way useChatUploadManager.tsx
// guards react-native-fs.
let RNBlobUtil: typeof import('react-native-blob-util').default;
if (Platform.OS !== 'web') {
  RNBlobUtil = require('react-native-blob-util').default;
}

type DownloadFilesContext = {
  selectedFiles: (Document | LocalFile | undefined)[];
  setSelectedFiles: React.Dispatch<
    React.SetStateAction<(Document | LocalFile | undefined)[]>
  >;
  downloadingErrorFiles: (Document | LocalFile | undefined)[];
  setDownloadingErrorFiles: React.Dispatch<
    React.SetStateAction<(Document | LocalFile | undefined)[]>
  >;
  downloadingSuccessFiles: (Document | LocalFile | undefined)[];
  setDownloadingSuccessFiles: React.Dispatch<
    React.SetStateAction<(Document | LocalFile | undefined)[]>
  >;
  downloadFile: () => void;
  downloadFiles: (
    files: (Document | LocalFile | undefined)[],
    isRetryDownloadErrorFiles?: boolean,
    isMedia?: boolean
  ) => void;
  showMsg: boolean;
  setShowMsg: React.Dispatch<React.SetStateAction<boolean>>;
  isDownloading: boolean;
  setIsDownloading: React.Dispatch<React.SetStateAction<boolean>>;
  downloadError: string | null;
  clearDownloadError: () => void;
  queueStatus: {
    activeDownloads: number;
    queueLength: number;
  };
};

const downloadFilesContext = createContext<DownloadFilesContext | undefined>(
  undefined
);

const globalDownloadQueue: string[] = [];
const MAX_CONCURRENT_DOWNLOADS = 3;
let activeDownloads = 0;

const acquireDownloadSlot = async (clientId: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const checkSlot = () => {
      if (activeDownloads < MAX_CONCURRENT_DOWNLOADS) {
        activeDownloads++;
        globalDownloadQueue.push(clientId);
        resolve(true);
      } else {
        setTimeout(checkSlot, 100);
      }
    };
    checkSlot();
  });
};

const releaseDownloadSlot = (clientId: string) => {
  activeDownloads = Math.max(0, activeDownloads - 1);
  const index = globalDownloadQueue.indexOf(clientId);
  if (index > -1) {
    globalDownloadQueue.splice(index, 1);
  }
};

export const DownloadFilesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { Provider } = downloadFilesContext;
  const { activeIndex, previewDocuments } = usePreviewDocuments();
  const [selectedFiles, setSelectedFiles] = useState<
    (Document | LocalFile | undefined)[]
  >([]);
  const [downloadingErrorFiles, setDownloadingErrorFiles] = useState<
    (Document | LocalFile | undefined)[]
  >([]);
  const [downloadingSuccessFiles, setDownloadingSuccessFiles] = useState<
    (Document | LocalFile | undefined)[]
  >([]);
  const [showMsg, setShowMsg] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const clearDownloadError = () => setDownloadError(null);
  const [downloadingSuccessFile, setDownloadingSuccessFile] = useState<
    Document | LocalFile | undefined
  >();
  const [downloadingErrorFile, setDownloadingErrorFile] = useState<
    Document | LocalFile | undefined
  >();
  const [isRetryDownloadErrorFiles, setIsRetryDownloadErrorFiles] =
    useState<boolean>(false);
  const [isMedia, setIsMedia] = useState<boolean>(false);

  useEffect(() => {
    if (
      (downloadingSuccessFiles.length > 0 ||
        downloadingErrorFiles.length > 0) &&
      selectedFiles.length === 0
    ) {
      setTimeout(
        () => {
          setIsDownloading(false);
          setShowMsg(false);
          setDownloadingSuccessFiles([]);
          setDownloadingErrorFiles([]);
        },
        downloadingErrorFiles.length > 0 ? 10000 : 5000
      );
    } else if (selectedFiles.length > 0 || downloadingSuccessFiles.length > 0) {
      !showMsg && downloadingSuccessFiles.length > 0 && setShowMsg(true);
    }
  }, [selectedFiles, downloadingSuccessFiles, downloadingErrorFiles]);

  const { saveImage } = useSaveImage();
  // TA-9921 (Copilot review): the boolean saveImage() returns must reach the
  // caller so a failed on-device save (e.g. the API<29 SAF picker denied, or
  // the write itself failing) can be reported as an error instead of the
  // local-cache-download success below silently standing in for it.
  const getFileInfo = async (
    item?: Document | LocalFile
  ): Promise<{
    fileInfo: FileSystem.FileInfo;
    imageSaved?: boolean;
  }> => {
    const file = item ?? previewDocuments?.[activeIndex];
    let fileURL;
    if (file.file.url) {
      fileURL = file.file.url;
    } else {
      fileURL = await AsyncStorage.getItem(file.clientId);
    }
    let imageSaved: boolean | undefined;
    if (isMedia && file?.contentType?.startsWith('image')) {
      imageSaved = await saveImage(fileURL, getFileName(file), false);
    }

    const fileUri = encodeURI(
      FileSystem.documentDirectory + (file ? getFileName(file) : '')
    );

    let fileInfo = await FileSystem.getInfoAsync(fileUri);
    if (!fileInfo.exists) {
      const baseTimeout = 30;
      const estimatedSizeMB = 10;
      const extraTime = estimatedSizeMB * 2;
      const timeoutDuration = Math.min(baseTimeout + extraTime, 180);

      const startTime = Date.now();

      try {
        await Promise.race([
          FileSystem.downloadAsync(file ? fileURL : '', fileUri),
          new Promise<null>((_, reject) =>
            setTimeout(
              () =>
                reject(
                  new Error(
                    `Download timeout: ${file?.clientId} after ${timeoutDuration}s`
                  )
                ),
              timeoutDuration * 1000
            )
          ),
        ]);

        const downloadTime = Date.now() - startTime;
        console.log(
          `Download completed in ${downloadTime}ms for file: ${file?.clientId}`
        );
      } catch (error) {
        const downloadTime = Date.now() - startTime;
        console.error(
          `Download failed after ${downloadTime}ms for file: ${file?.clientId}`,
          error
        );
        setDownloadingErrorFile(file);
        return { fileInfo, imageSaved };
      }
    }
    fileInfo = await FileSystem.getInfoAsync(fileUri);
    if (!fileInfo.exists) {
      setDownloadingErrorFile(file);
    }
    return { fileInfo, imageSaved };
  };

  const getFileName = (item?: Document | LocalFile) => {
    const file = item ?? previewDocuments?.[activeIndex];
    let name = '';
    switch (file?.contentType) {
      case 'audio/m4a':
        name = file?.id + '.m4a';
        break;
      case 'audio/caf':
        name = file?.id + '.caf';
        break;
      case 'application/octet-stream':
        if (file?.name) {
          name = file?.name + '.png';
        } else {
          name = file?.id + '.png';
        }
        break;
      default:
        name = file ? file?.name : '';
    }
    return name;
  };

  useEffect(() => {
    if (!isDownloading && selectedFiles.length > 0) {
      setIsDownloading(true);

      Platform.OS === 'web'
        ? (async () => await downloadFilesWeb(selectedFiles as Document[]))()
        : selectedFiles.map(async (file) => {
            await doDownloadFile(file);
          });
    }
  }, [selectedFiles]);

  useEffect(() => {
    setDownloadingSuccessFiles([
      ...downloadingSuccessFiles,
      downloadingSuccessFile,
    ]);

    if (downloadingSuccessFile && isRetryDownloadErrorFiles) {
      const downloadingErrorFiles1 = downloadingErrorFiles.filter(
        (f) => f?.id !== downloadingSuccessFile?.id
      );
      setDownloadingErrorFiles(downloadingErrorFiles1);
    }

    const selectedFiles1 = selectedFiles.filter(
      (f) => downloadingSuccessFile?.id !== f?.id
    );
    setSelectedFiles(selectedFiles1);
  }, [downloadingSuccessFile]);

  useEffect(() => {
    setDownloadingErrorFiles([...downloadingErrorFiles, downloadingErrorFile]);
    const selectedFiles1 = selectedFiles.filter(
      (f) => downloadingErrorFile?.id !== f?.id
    );
    setSelectedFiles(selectedFiles1);
  }, [downloadingErrorFile]);

  const downloadFiles = useCallback(
    (
      files: (Document | LocalFile | undefined)[],
      isRetryDownloadErrorFiles?: boolean,
      isMedia?: boolean
    ) => {
      setIsRetryDownloadErrorFiles(!!isRetryDownloadErrorFiles);
      setIsMedia(!!isMedia);
      setSelectedFiles((prev) => [...prev, ...files]);
    },
    []
  );

  const downloadFile = useCallback(() => {
    previewDocuments &&
      setSelectedFiles((prev) => [...prev, previewDocuments[activeIndex]]);
  }, [previewDocuments, activeIndex]);

  const downloadBlob = (blob: Blob, fileName: string) => {
    if (Platform.OS !== 'web') return;

    const url = URL.createObjectURL(blob);
    if (!url) return;
    triggerAnchorDownload(url, fileName);
    // Revoke on the next tick: revoking synchronously can race the browser's
    // download handoff for the object URL on some engines (TA-10292).
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  // Direct browser-navigation download for a (possibly cross-origin) URL.
  // Presigned S3 URLs already return `content-disposition: attachment`, so the
  // browser downloads them without us reading the bytes via fetch — this avoids
  // the CORS `TypeError: Failed to fetch` that blocks blob-based downloads
  // (TA-10038). The `download` attribute is ignored cross-origin, but S3's
  // content-disposition supplies the filename.
  const triggerAnchorDownload = (url: string, fileName: string) => {
    if (Platform.OS !== 'web') return;

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // URL for direct browser-navigation downloads (single file, or the
  // single-failure zip fallback below). Must be the raw S3 presigned URL:
  // its response carries a server-set `content-disposition:
  // attachment; filename="<doc name>"` (file_type.rb), which is what
  // supplies the correct filename since the `download` attribute is
  // ignored cross-origin. The ImageKit CDN URL has no such override, so
  // using it here would silently rename downloads to the origin object's
  // key instead of the user-facing document name.
  const resolveDirectDownloadURL = async (item: Document) =>
    (await AsyncStorage.getItem(item.clientId)) || item.file?.url;

  // Whether a document's zip fetch should go through the ImageKit CDN
  // instead of raw S3. Unlike the presigned S3 URL, the CDN URL never
  // expires and reliably returns permissive CORS headers, so `fetch` works
  // regardless of S3 bucket CORS config or presigned-URL staleness
  // (TA-10292). Filename doesn't matter for this path — JSZip names each
  // zip entry from `document.name` — so the CDN URL's lack of a filename
  // override is irrelevant here. Only images are routed through the CDN
  // today — non-image originals (PDF, etc.) keep the S3 path until CDN
  // delivery is verified for those content types.
  const isCdnFetchEligible = (item: Document) =>
    !!(item.isImage && item.file?.cdnBaseUrl && item.file?.path);

  const resolveZipFetchURL = async (item: Document) =>
    isCdnFetchEligible(item)
      ? `${item.file!.cdnBaseUrl}${item.file!.path}?ik-attachment=true`
      : resolveDirectDownloadURL(item);

  const finishWebDownload = () => {
    setIsDownloading(false);
    setSelectedFiles([]);
  };

  const downloadFilesWeb = async (items: Document[]) => {
    if (items?.length === 0) return;
    if (Platform.OS !== 'web') return;

    // Single file: navigate the browser straight to the presigned URL. No
    // fetch/blob, so no cross-origin CORS failure.
    if (items?.length === 1) {
      const singleItem = items?.[0];
      const fileURL = await resolveDirectDownloadURL(singleItem);
      if (!fileURL) {
        setDownloadError('files.downloadFileNotFound');
        finishWebDownload();
        return;
      }

      triggerAnchorDownload(fileURL, singleItem?.name);
      finishWebDownload();
      return;
    }

    // Multiple files: attempt a client-side zip (nice single artifact when the
    // bytes are readable), but stay resilient — a single file that can't be
    // fetched (e.g. S3 CORS) must not abort the whole batch. Failed fetches
    // fall back to a direct browser download so the user still gets the file.
    const itemsNewFormatted = items?.map((fileItem, index) => {
      const splitName = fileItem.name?.split('.');
      return {
        ...fileItem,
        name: `${splitName?.[0] || 'file'}${index}.${splitName?.[1] || ''}`,
      };
    });

    const zip = new JSZip();
    let zippedCount = 0;
    const missingFiles: Document[] = [];
    const fetchFailedFiles: { url: string; name: string }[] = [];

    for (const document of itemsNewFormatted) {
      const zipFetchURL = await resolveZipFetchURL(document);
      if (!zipFetchURL) {
        missingFiles.push(document);
        continue;
      }

      try {
        const response = await fetchWithErrorHandling(zipFetchURL);
        const blob = await response.blob();
        zip.file(document?.name, blob);
        zippedCount++;
      } catch (error) {
        // Most likely a cross-origin CORS block or an expired presigned URL
        // — collect it rather than falling back immediately. Firing a
        // programmatic anchor download per failed file in this loop used to
        // mean several near-simultaneous `a.click()` calls; browsers only
        // honor the first, so a batch with more than one failure silently
        // looked like "only one image downloads" (TA-10292).
        console.warn(`Zip fetch failed for ${document?.name}`, error);
        // Only re-resolve when the failed fetch used the CDN URL: reusing
        // it for the anchor fallback would both fail again and lack the S3
        // content-disposition filename. A raw S3/AsyncStorage URL that just
        // failed the fetch is still the correct anchor-download target, so
        // there's no need to look it up again.
        const directURL = isCdnFetchEligible(document)
          ? await resolveDirectDownloadURL(document)
          : zipFetchURL;
        if (directURL) {
          fetchFailedFiles.push({ url: directURL, name: document?.name });
        } else {
          missingFiles.push(document);
        }
      }
    }

    let zipGenerationFailed = false;
    if (zippedCount > 0) {
      try {
        const zipContent = await zip.generateAsync({ type: 'blob' });
        downloadBlob(zipContent, 'documents.zip');
      } catch (error) {
        console.error('Zip generation failed:', error);
        zipGenerationFailed = true;
      }
    }

    // A single failed fetch can still be delivered as a direct download —
    // browsers allow one programmatic download without throttling it. More
    // than one is unreliable (browsers block/drop all but the first), so
    // surface an explicit error instead of silently dropping files.
    if (fetchFailedFiles.length === 1) {
      const [failed] = fetchFailedFiles;
      triggerAnchorDownload(failed.url, failed.name);
    }

    // Exactly one error surfaces, prioritized by severity, instead of
    // letting later checks silently overwrite earlier ones: losing the
    // whole zip after a successful fetch is worse than an unresolvable
    // file, which is worse than a handled partial-fetch failure.
    if (zipGenerationFailed) {
      setDownloadError('files.downloadNetworkError');
    } else if (missingFiles.length > 0) {
      setDownloadError('files.downloadFileNotFound');
    } else if (fetchFailedFiles.length > 1) {
      setDownloadError('files.downloadPartialFailure');
    }

    finishWebDownload();
  };

  const doDownloadFile = async (item?: Document | LocalFile) => {
    const file = item ?? previewDocuments?.[activeIndex];
    const clientId = file?.clientId || 'unknown';

    await acquireDownloadSlot(clientId);

    try {
      const { fileInfo, imageSaved } = await getFileInfo(file);
      if (fileInfo.exists) {
        if (isMedia && file?.contentType?.startsWith('image')) {
          // TA-9921 (Copilot review): the local cache file existing only
          // proves the in-app preview download succeeded -- it says nothing
          // about whether saveImage() actually got the file onto the
          // device (MediaStore on API 29+, the SAF picker/write on <29).
          // Report the real outcome instead of always claiming success.
          if (imageSaved) {
            setDownloadingSuccessFile(file);
          } else {
            setDownloadingErrorFile(file);
          }
          return;
        }

        // TA-9921 (Copilot review): setDownloadingSuccessFile must not fire
        // until the on-device save actually succeeds -- calling it
        // unconditionally here (as the image branch above used to) let a
        // failed MediaStore/SAF write still get reported as a success, and
        // a subsequent setDownloadingErrorFile call (below, or via the
        // outer catch) then leaves the file in BOTH lists at once.
        if (Platform.OS === 'android') {
          const fileName = getFileName(file);
          const mimeType =
            file?.contentType ||
            mime.getType(fileName) ||
            'application/octet-stream';

          // WRITE_EXTERNAL_STORAGE / READ_EXTERNAL_STORAGE are permanently
          // blocked from the manifest (app.config.js blockedPermissions,
          // TA-10087 -- Google Play rejected a build over broad media
          // permissions), so there is no legacy-permission fallback below
          // API 29; MediaCollection.copyToMediaStore itself is API 29+ only
          // (Copilot review, PR #5298). Below that, route through Storage
          // Access Framework instead (see androidLegacyDownloadDir.ts for
          // why SAF is scoped to <29 only, not used universally).
          if (Number(Platform.Version) < 29) {
            // fileUri (getFileInfo) is built with encodeURI, so fileInfo.uri
            // can carry percent escapes -- saveToLegacyAndroidDownloads
            // decodes it before reading.
            const saved = await saveToLegacyAndroidDownloads(
              fileInfo.uri,
              fileName,
              mimeType
            );
            if (saved) {
              setDownloadingSuccessFile(file);
            } else {
              setDownloadingErrorFile(file);
            }
            return;
          }

          await RNBlobUtil.MediaCollection.copyToMediaStore(
            {
              name: fileName,
              parentFolder: '',
              mimeType,
            },
            'Download',
            // fileUri (getFileInfo) is built with encodeURI, so fileInfo.uri
            // can carry percent escapes (e.g. a space as %20); decode before
            // handing RNBlobUtil a native path, or filenames with spaces or
            // non-ASCII characters fail to copy (Copilot review, PR #5298).
            decodeURI(fileInfo.uri.replace('file://', ''))
          );
          setDownloadingSuccessFile(file);
        } else {
          await Sharing.shareAsync(fileInfo.uri);
          setDownloadingSuccessFile(file);
        }
      }
    } catch (error) {
      console.error(`Download failed for ${clientId}:`, error);
      setDownloadingErrorFile(file);
    } finally {
      releaseDownloadSlot(clientId);
    }
  };

  return (
    <Provider
      value={{
        selectedFiles,
        setSelectedFiles,
        downloadingSuccessFiles,
        setDownloadingSuccessFiles,
        downloadingErrorFiles,
        setDownloadingErrorFiles,
        downloadFile,
        downloadFiles,
        showMsg,
        setShowMsg,
        isDownloading,
        setIsDownloading,
        downloadError,
        clearDownloadError,
        queueStatus: {
          activeDownloads,
          queueLength: globalDownloadQueue.length,
        },
      }}>
      {children}
    </Provider>
  );
};

const useDownloadFiles = (): DownloadFilesContext => {
  const context = useContext(downloadFilesContext);
  if (context === undefined) {
    throw new Error('useDownloadFiles must be used within a Provider');
  }
  return context;
};

export default useDownloadFiles;
